import React, { useState, useContext } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "react-router-dom";

import he from "he";

import { AppContext } from "../../context/AppContext";
import Pack from "../Pack";
import SimpleAlert from "../SimpleAlert";
import sendEvent from "../../utils/functions/sendEvent";
import flameIcon from "../../img/list.svg";

import styles from "../../css/reviews/ReviewsForm.module.css";
import base from "../../css/reviews/Reviews.module.css";

// Poptávkový formulář pro Widget s recenzemi.
// Logika převzata z ModalConsultation (POST na /api/send-message/),
// ale vykreslená jako inline sekce (kotva pro CTA scroll), ne modal.
const ReviewsForm = ({ id = "reviews-form" }) => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const { isLogged, showAlert, closeAlert, isAlertVisible, alertMessage } =
    useContext(AppContext);

  const content = t("reviews.form", { returnObjects: true });

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [message, setMessage] = useState("");
  const [checkbox, setCheckbox] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkbox) return;

    try {
      const dataToSend = {
        name: company,
        email: email,
        language: lng,
        message: `
            produkt: Widget s recenzemi<br>
            e-mail: ${email}<br>
            název společnosti: ${company}<br>
            ${website ? "web e-shopu: " + website + "<br>" : ""}
            ${phone ? "telefon: " + phone + "<br>" : ""}
            ${name ? "jméno: " + name + "<br>" : ""}
            ${surname ? "příjmení: " + surname + "<br>" : ""}
            ${message ? "zpráva: " + message : ""}
        `,
        subject: "PermoniQ – Widget recenzí",
        send_email: true,
        country: window.location.pathname.split("/")[1],
      };

      const response = await fetch("https://permoniq.com/api/send-message/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        showAlert(t("consultation.errorMessages.consultationErrorMsg"), "danger");
        const errorDetails = await response.json();
        throw new Error(
          `Network response was not ok: ${response.status} ${response.statusText} - ${JSON.stringify(
            errorDetails
          )}`
        );
      }

      showAlert(t("consultation.infoMessages.requestSentInfoMsg"), "info");

      setEmail("");
      setPhone("");
      setName("");
      setSurname("");
      setCompany("");
      setWebsite("");
      setMessage("");
      setCheckbox(false);
      sendEvent("reviews_widget_request");
    } catch (error) {
      showAlert(t("consultation.errorMessages.consultationErrorMsg"), "danger");
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        errorObject: error,
      });
    }
  };

  return (
    <>
      {isAlertVisible && (
        <SimpleAlert
          message={alertMessage.text}
          type={alertMessage.type}
          onClose={closeAlert}
        />
      )}
      <Pack idSection={id} dataComponent={"reviews-form"}>
        <h2 className={`title ${base.heading}`}>{content.heading}</h2>
        <div className={styles.layout}>
          <div className={styles.formBox}>
            <form autoComplete="off" onSubmit={handleSubmit}>
              <div className={styles.field}>
                <label htmlFor="reviews-email">
                  {content.emailLabel}
                  <span className={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  id="reviews-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className={`${styles.row} ${styles.field}`}>
                <div>
                  <label htmlFor="reviews-company">
                    {content.companyLabel}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="reviews-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reviews-website">{content.websiteLabel}</label>
                  <input
                    type="text"
                    id="reviews-website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
              </div>

              <div className={`${styles.row} ${styles.field}`}>
                <div>
                  <label htmlFor="reviews-name">{content.nameLabel}</label>
                  <input
                    type="text"
                    id="reviews-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="reviews-surname">{content.surnameLabel}</label>
                  <input
                    type="text"
                    id="reviews-surname"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="reviews-phone">{content.phoneLabel}</label>
                <input
                  type="tel"
                  id="reviews-phone"
                  value={phone}
                  onChange={(e) => {
                    const phoneInput = e.target.value;
                    if (/^(\+)?[\d\s]*$/.test(phoneInput)) {
                      setPhone(phoneInput);
                    }
                  }}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="reviews-message">{content.messageLabel}</label>
                <textarea
                  id="reviews-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              <label className={styles.gdpr}>
                <input
                  type="checkbox"
                  checked={checkbox}
                  onChange={(e) => setCheckbox(e.target.checked)}
                  required
                />
                <span>
                  <Trans
                    i18nKey={t("consultation.checkboxLabel")}
                    components={[
                      <a
                        href={`${window.location.origin}/${lng}/terms-of-service/`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        terms
                      </a>,
                    ]}
                  />
                  <span className={styles.required}>*</span>
                </span>
              </label>

              <button
                type="submit"
                className={styles.submit}
                disabled={!checkbox}
              >
                {he.decode(content.submitButton)}
              </button>
            </form>
          </div>

          <div className={styles.infoBox}>
            <p className={styles.price}>{content.price}</p>
            <p className={styles.priceNote}>{content.priceNote}</p>
            <ul className={styles.infoList}>
              {Array.isArray(content.perks) &&
                content.perks.map((perk, index) => (
                  <li key={index}>
                    <img className={styles.check} src={flameIcon} alt="" />
                    <span>{perk}</span>
                  </li>
                ))}
            </ul>
            <div className={styles.contact}>
              <a
                href={`${
                  isLogged
                    ? t("permoniqPhoneHref")
                    : t("permoniqPhoneBusinessHref")
                }`}
              >
                {`${isLogged ? t("permoniqPhone") : t("permoniqPhoneBusiness")}`}
              </a>
              <a href="mailto:info@permoniq.com">info@permoniq.com</a>
            </div>
          </div>
        </div>
      </Pack>
    </>
  );
};

export default ReviewsForm;
